import { createContext, useContext, useState, useEffect, type ReactNode } from "react";
import en, { type Translations } from "./en";
import da from "./da";

export type Lang = "en" | "da";

const translations: Record<Lang, Translations> = { en, da };

interface I18nContext {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: Translations;
}

const Ctx = createContext<I18nContext>({
  lang: "en",
  setLang: () => {},
  t: en,
});

function detectLang(): Lang {
  try {
    const stored = localStorage.getItem("lang") as Lang | null;
    if (stored && translations[stored]) return stored;

    const browserLang = navigator.language?.split("-")[0];
    if (browserLang === "da") return "da";
  } catch {
    // SSR or restricted access
  }
  return "en";
}

export const I18nProvider = ({ children }: { children: ReactNode }) => {
  const [lang, setLangState] = useState<Lang>(detectLang);

  const setLang = (l: Lang) => {
    setLangState(l);
    try {
      localStorage.setItem("lang", l);
    } catch {}
    document.documentElement.lang = l;
  };

  useEffect(() => {
    document.documentElement.lang = lang;
  }, []);

  return (
    <Ctx.Provider value={{ lang, setLang, t: translations[lang] }}>
      {children}
    </Ctx.Provider>
  );
};

export const useTranslation = () => useContext(Ctx);
